import { Link, useParams, Navigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Subpage, BeforeAfter, OrderCta } from "@/components/site/Subpage";
import { Seo, SITE_URL } from "@/components/site/Seo";
import { Label } from "@/components/site/Reveal";
import { findField, FIELDS } from "@/content/fields";
import { POSTS } from "@/content/blog";

export default function FieldPage() {
  const { field } = useParams();
  const f = findField(field);
  if (!f) return <Navigate to="/" replace />;
  const jsonLd = {
    "@context": "https://schema.org", "@type": "Service", name: `Resume bullet rewriting for ${f.name.toLowerCase()}`,
    serviceType: "Resume writing", description: f.description, url: `${SITE_URL}/resume-help/${f.slug}`,
    provider: { "@type": "ProfessionalService", name: "Verbulat", url: `${SITE_URL}/` },
  };
  return (
    <Subpage>
      <Seo title={f.title} description={f.description} path={`/resume-help/${f.slug}`} jsonLd={jsonLd} />
      <div data-testid={`field-page-${f.slug}`}>
        <Label>Resume help by field</Label>
        <h1 className="font-serif-d mt-5 text-5xl leading-[0.95] text-white sm:text-7xl">{f.h1}</h1>
        <p className="mt-6 text-lg leading-relaxed text-slate-300">{f.intro}</p>
        <h2 className="font-serif-d mt-14 text-3xl text-white">What we bring out in your bullets</h2>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {f.focus.map((x) => (
            <li key={x} className="flex gap-3 rounded-2xl border border-white/10 bg-[var(--navy)] p-4 text-slate-200">
              <Check className="mt-0.5 h-5 w-5 shrink-0 text-[var(--teal)]" />{x}
            </li>
          ))}
        </ul>
        <h2 className="font-serif-d mt-14 text-3xl text-white">Before and after</h2>
        {f.examples.map((e) => <BeforeAfter key={e.before} before={e.before} after={e.after} />)}
        <p className="text-xs text-slate-400">Illustrative examples. In your rewrite, every number comes from you, and nothing is invented.</p>
        <h2 className="font-serif-d mt-14 text-3xl text-white">How it works</h2>
        <ol className="mt-5 space-y-3 text-slate-200">
          <li><span className="font-mono-d mr-3 text-[var(--teal)]">01</span>Send your resume and the {f.name.toLowerCase()} role you're targeting.</li>
          <li><span className="font-mono-d mr-3 text-[var(--teal)]">02</span>We draft action-verb bullets with measurable results.</li>
          <li><span className="font-mono-d mr-3 text-[var(--teal)]">03</span>We ask you for real numbers and match keywords from your target postings.</li>
          <li><span className="font-mono-d mr-3 text-[var(--teal)]">04</span>You get a clean Word document and PDF.</li>
        </ol>
        <OrderCta text={`Ready to rewrite your ${f.name.toLowerCase().replace(/s$/, "")} resume?`} />
        <div className="mt-14 grid gap-8 sm:grid-cols-2">
          <div>
            <p className="font-mono-d text-xs uppercase tracking-[0.2em] text-slate-400">Other fields</p>
            <ul className="mt-3 space-y-2">
              {FIELDS.filter((x) => x.slug !== f.slug).map((x) => (
                <li key={x.slug}><Link to={`/resume-help/${x.slug}`} className="text-slate-200 hover:text-[var(--teal)]">Resume help for {x.name.toLowerCase()}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-mono-d text-xs uppercase tracking-[0.2em] text-slate-400">Bullet tips</p>
            <ul className="mt-3 space-y-2">
              {POSTS.slice(0, 3).map((p) => (
                <li key={p.slug}><Link to={`/blog/${p.slug}`} className="text-slate-200 hover:text-[var(--teal)]">{p.title}</Link></li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Subpage>
  );
}
